import { useEffect } from 'react';
import { Time } from './MomentTime';
import { DayMapping } from './constants';

export const getDateTime = (date) => {
  return Time.University(date).format('MMMM D, YYYY, h:mm A');
};

// Redirects the user to old portal
export const stopPreview = (href) => {
  // delete the preview cookie
  document.cookie =
    'portal-preview=; Path=/; Domain=.odu.edu; Expires=Thu, 01 Jan 1970 23:59:59 GMT';
  // navigate to old portal
  window.location.href = href;
};

//Θ(N) where N is the number of space separated tokens in a string
//clean up the id string by converting to lower case, remove any special chars except '_' and trim
export const cleanUpID = (id) => {
  if (!id) return '';

  return (
    id
      ?.toLocaleLowerCase()
      ?.trim()
      ?.replace(/[^a-zA-Z0-9_ ]/g, '')
      ?.split(/\s/g)
      ?.map((token, index) =>
        index ? (token?.[0]?.toLocaleUpperCase() ?? '') + (token?.slice(1) ?? '') : token,
      )
      ?.join('') ?? ''
  );
};

//Θ(NxM) where N is the number of nested objects and M is the number of attributes in each nested object
//Deep copies an object to avoid problems with referenced data
export const deepCloneObject = (object) => {
  return Object.keys(object).reduce((clone, current) => {
    clone[current] =
      object[current]?.constructor === Object ? deepCloneObject(object[current]) : object[current];
    return clone;
  }, {});
};

// Convert days code (ex. MWF) to full name (ex. Monday, Wednesday and Friday)
export const convertDayCodesToFullNames = (dayCodes) => {
  const fullNames = Array.from(dayCodes).map((code) => DayMapping[code] || code);

  if (fullNames.length > 1) {
    const lastDay = fullNames.pop();
    return `${fullNames.join(', ')} and ${lastDay}`;
  }

  return fullNames.join(', ');
};

export const isHTML = (content) => /<\/?[a-z][\s\S]*>/i.test(content);
//Swaps the array elements at index1 & index2
export const swap = (array, index1, index2) => {
  let temp = array[index1];
  array[index1] = array[index2];
  array[index2] = temp;

  return array;
};

//θ(N X M) where N is the number of words and M is the number of characters in each word
//Converts the string to title case
export const toTitleCase = (str) => {
  str = str.toLowerCase().split(' ');
  for (var i = 0; i < str.length; i++) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
  }
  return str.join(' ');
};

/**
 * A custom hook that intercepts window.open when there are unsaved changes.
 *
 * @param {boolean} isDirty - Flag indicating if there are unsaved changes.
 * @param {Function} setOpenUnsavedChangesModal - Function to toggle the unsaved changes modal.
 * @param {string|null} pendingExternalUrl - The URL saved from an intercepted external navigation.
 * @param {Function} setPendingExternalUrl - Function to set or clear the pending external URL.
 */
export const useInterceptWindowOpen = ({
  isDirty,
  setOpenUnsavedChangesModal,
  pendingExternalUrl,
  setPendingExternalUrl,
}) => {
  // Effect to override window.open when isDirty changes.
  useEffect(() => {
    // Save the original window.open so we can restore it later.
    const originalWindowOpen = window.open;

    // Override window.open.
    window.open = (url, target, features) => {
      if (isDirty) {
        // Save the URL and open the unsaved changes modal.
        setPendingExternalUrl(url);
        setOpenUnsavedChangesModal(true);
        return null; // Prevent the navigation.
      }
      // If not dirty, call the original window.open.
      return originalWindowOpen.call(window, url, target, features);
    };

    // Cleanup: restore the original window.open.
    return () => {
      window.open = originalWindowOpen;
    };
  }, [isDirty, setOpenUnsavedChangesModal, setPendingExternalUrl]);

  // Effect to automatically open the pending external URL when isDirty becomes false.
  useEffect(() => {
    if (!isDirty && pendingExternalUrl) {
      // Open the external URL in a new tab.
      window.open(pendingExternalUrl, '_blank');
      // Clear the pending URL once opened.
      setPendingExternalUrl(null);
    }
  }, [isDirty, pendingExternalUrl, setPendingExternalUrl]);
};

/**
 * Starts a polling process that repeatedly executes a fetch function at a specified interval
 * until a validation condition is met or manually stopped.
 *
 * @param {Object} params - The parameters for polling.
 * @param {Function} params.fetchFn - An asynchronous function that fetches data on each poll iteration.
 * @param {Function} [params.validate=() => false] - A function that validates the fetched result.
 *        If it returns true, polling stops and the result is returned.
 * @param {number} [params.interval=2000] - The time interval (in milliseconds) between each poll.
 * @param {number} [params.preDelay=0] - Optional initial delay (in milliseconds) before polling starts.
 *
 * @returns {Function} stopPolling - A function that can be called to manually stop the polling process.
 *
 */
export const pollingStatus = ({
  fetchFn,
  validate = () => false,
  interval = 2000,
  preDelay = 0,
}) => {
  let isPolling = true;

  const stopPolling = () => {
    isPolling = false; // Set flag to stop polling
  };

  const poll = async () => {
    if (preDelay) {
      await new Promise((resolve) => setTimeout(resolve, preDelay)); // Initial delay before polling starts
    }

    while (isPolling) {
      try {
        const result = await fetchFn();

        if (validate(result)) {
          stopPolling(); // Ensure polling stops if condition is met
          return result;
        }
      } catch (err) {
        console.error('Polling error:', err);
      }

      if (isPolling) {
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    }
  };

  poll(); // Start polling in the background
  return stopPolling; // Return function to stop polling
};
